import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import { locations } from '../../mocks/categories';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';

export default function PostAdPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showListingTypeModal, setShowListingTypeModal] = useState(true);
  const [listingType, setListingType] = useState<'selling' | 'buying' | null>(null);
  const [userProfile, setUserProfile] = useState<any>(null);
  
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    price: '',
    location: '',
    description: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    jobType: '',
    experienceLevel: '',
    salaryRange: '',
    vehicleCondition: '',
    vehicleYear: '',
    vehicleMake: '',
    propertyType: '',
    bedrooms: '',
    bathrooms: '',
    condition: '',
    brand: '',
    // Buyer-specific fields
    urgency: '',
    skills: '',
    availability: ''
  });

  const [images, setImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch user profile information
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (user) {
        try {
          const { data: profile, error } = await supabase
            .from('profiles')
            .select('full_name, phone, email')
            .eq('id', user.id)
            .single();

          if (profile && !error) {
            setUserProfile(profile);
            // Pre-fill contact information
            setFormData(prev => ({
              ...prev,
              contactName: profile.full_name || '',
              contactPhone: profile.phone || '',
              contactEmail: profile.email || user.email || ''
            }));
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
          // Fallback to user auth data
          setFormData(prev => ({
            ...prev,
            contactName: user.user_metadata?.full_name || '',
            contactEmail: user.email || ''
          }));
        }
      }
    };

    fetchUserProfile();
  }, [user]);

  const handleListingTypeSelect = (type: 'selling' | 'buying') => {
    setListingType(type);
    setShowListingTypeModal(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validate required fields
      if (!formData.title || !formData.category || !formData.location || !formData.description) {
        alert('Please fill in all required fields');
        setIsSubmitting(false);
        return;
      }

      if (listingType === 'selling' && !formData.price) {
        alert('Please enter a price for your item');
        setIsSubmitting(false);
        return;
      }

      // Prepare the ad data for submission
      const adData = {
        ...formData,
        // Convert formatted price back to number for database storage
        price: formData.price && /^\d+/.test(formData.price.replace(/,/g, '')) 
          ? parseFloat(formData.price.replace(/,/g, '')) 
          : null,
        listing_type: listingType,
        images: imagePreviews // Use the image preview URLs
      };

      // If user is logged in, save to database
      if (user) {
        const { error } = await supabase
          .from('ads')
          .insert([{
            ...adData,
            user_id: user.id,
            status: 'active',
            views: 0,
            favorites: 0,
            expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() // 30 days from now
          }]);

        if (error) throw error;
      }

      // Navigate to success page
      navigate('/ad-success', { 
        state: { 
          adData: {
            ...adData,
            price: formData.price // Keep formatted price for display
          }
        } 
      });

    } catch (error) {
      console.error('Error submitting ad:', error);
      alert('Failed to submit ad. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Format price input with commas
    if (name === 'price') {
      // Remove all non-digit characters except letters for text like "Contact for price"
      const isNumeric = /^\d+$/.test(value.replace(/,/g, ''));
      
      if (isNumeric || value === '') {
        // Format numbers with commas
        const numericValue = value.replace(/,/g, '');
        const formattedValue = numericValue ? parseInt(numericValue).toLocaleString() : '';
        setFormData(prev => ({ ...prev, [name]: formattedValue }));
      } else {
        // Allow text inputs like "Contact for price", "Negotiable"
        setFormData(prev => ({ ...prev, [name]: value }));
      }
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      handleFiles(Array.from(files));
    }
  };

  const handleFiles = (files: File[]) => {
    const validFiles = files.filter(file => file.type.startsWith('image/'));
    const remainingSlots = 10 - images.length;
    const filesToAdd = validFiles.slice(0, remainingSlots);

    if (filesToAdd.length > 0) {
      const newImages = [...images, ...filesToAdd];
      setImages(newImages);

      filesToAdd.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreviews(prev => [...prev, reader.result as string]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  const renderCategorySpecificFields = () => {
    const isSelling = listingType === 'selling';
    const isBuying = listingType === 'buying';

    switch (formData.category) {
      case 'jobs':
        if (isSelling) {
          // Employer posting a job
          return (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Job Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="jobType"
                  value={formData.jobType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent pr-8"
                >
                  <option value="">Select job type</option>
                  <option value="Full-time">Full-time</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Contract">Contract</option>
                  <option value="Freelance">Freelance</option>
                  <option value="Internship">Internship</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Experience Level Required <span className="text-red-500">*</span>
                </label>
                <select
                  name="experienceLevel"
                  value={formData.experienceLevel}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent pr-8"
                >
                  <option value="">Select experience level</option>
                  <option value="Entry Level">Entry Level</option>
                  <option value="1-3 years">1-3 years</option>
                  <option value="3-5 years">3-5 years</option>
                  <option value="5+ years">5+ years</option>
                  <option value="Senior Level">Senior Level</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Salary Range (₦) <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  name="salaryRange"
                  value={formData.salaryRange}
                  onChange={handleChange}
                  placeholder="e.g., 500,000 - 800,000 per month"
                  required
                />
              </div>
            </>
          );
        } else {
          // Job seeker looking for work
          return (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Job Type Seeking <span className="text-red-500">*</span>
                </label>
                <select
                  name="jobType"
                  value={formData.jobType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent pr-8"
                >
                  <option value="">Select job type</option>
                  <option value="Full-time">Full-time</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Contract">Contract</option>
                  <option value="Freelance">Freelance</option>
                  <option value="Internship">Internship</option>
                  <option value="Any">Any</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Experience Level <span className="text-red-500">*</span>
                </label>
                <select
                  name="experienceLevel"
                  value={formData.experienceLevel}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent pr-8"
                >
                  <option value="">Select your experience</option>
                  <option value="Entry Level">Entry Level / Fresh Graduate</option>
                  <option value="1-3 years">1-3 years experience</option>
                  <option value="3-5 years">3-5 years experience</option>
                  <option value="5+ years">5+ years experience</option>
                  <option value="Senior Level">Senior Level</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Skills & Qualifications <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="skills"
                  value={formData.skills}
                  onChange={handleChange}
                  rows={3}
                  required
                  placeholder="List your key skills, certifications, and qualifications"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Availability <span className="text-red-500">*</span>
                </label>
                <select
                  name="availability"
                  value={formData.availability}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent pr-8"
                >
                  <option value="">Select availability</option>
                  <option value="Immediate">Immediate</option>
                  <option value="2 weeks notice">2 weeks notice</option>
                  <option value="1 month notice">1 month notice</option>
                  <option value="Flexible">Flexible</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expected Salary (₦)
                </label>
                <Input
                  type="text"
                  name="salaryRange"
                  value={formData.salaryRange}
                  onChange={handleChange}
                  placeholder="e.g., 500,000 per month or Negotiable"
                />
              </div>
            </>
          );
        }

      case 'cars':
        if (isSelling) {
          return (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vehicle Condition <span className="text-red-500">*</span>
                </label>
                <select
                  name="vehicleCondition"
                  value={formData.vehicleCondition}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent pr-8"
                >
                  <option value="">Select condition</option>
                  <option value="Brand New">Brand New</option>
                  <option value="Foreign Used">Foreign Used</option>
                  <option value="Nigerian Used">Nigerian Used</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Year <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  name="vehicleYear"
                  value={formData.vehicleYear}
                  onChange={handleChange}
                  placeholder="e.g., 2020"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Make/Brand <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  name="vehicleMake"
                  value={formData.vehicleMake}
                  onChange={handleChange}
                  placeholder="e.g., Toyota, Honda, Mercedes"
                  required
                />
              </div>
            </>
          );
        } else {
          // Looking to buy a car
          return (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Condition
                </label>
                <select
                  name="vehicleCondition"
                  value={formData.vehicleCondition}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent pr-8"
                >
                  <option value="">Any condition</option>
                  <option value="Brand New">Brand New</option>
                  <option value="Foreign Used">Foreign Used</option>
                  <option value="Nigerian Used">Nigerian Used</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Year Range
                </label>
                <Input
                  type="text"
                  name="vehicleYear"
                  value={formData.vehicleYear}
                  onChange={handleChange}
                  placeholder="e.g., 2018-2023"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Make/Brand
                </label>
                <Input
                  type="text"
                  name="vehicleMake"
                  value={formData.vehicleMake}
                  onChange={handleChange}
                  placeholder="e.g., Toyota, Honda, Mercedes or Any"
                />
              </div>
            </>
          );
        }

      case 'real-estate':
        if (isSelling) {
          return (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Property Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="propertyType"
                  value={formData.propertyType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent pr-8"
                >
                  <option value="">Select property type</option>
                  <option value="House for Sale">House for Sale</option>
                  <option value="House for Rent">House for Rent</option>
                  <option value="Apartment for Sale">Apartment for Sale</option>
                  <option value="Apartment for Rent">Apartment for Rent</option>
                  <option value="Land for Sale">Land for Sale</option>
                  <option value="Commercial Property">Commercial Property</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bedrooms <span className="text-red-500">*</span>
                </label>
                <select
                  name="bedrooms"
                  value={formData.bedrooms}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent pr-8"
                >
                  <option value="">Select bedrooms</option>
                  <option value="Studio">Studio</option>
                  <option value="1">1 Bedroom</option>
                  <option value="2">2 Bedrooms</option>
                  <option value="3">3 Bedrooms</option>
                  <option value="4">4 Bedrooms</option>
                  <option value="5+">5+ Bedrooms</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bathrooms <span className="text-red-500">*</span>
                </label>
                <select
                  name="bathrooms"
                  value={formData.bathrooms}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent pr-8"
                >
                  <option value="">Select bathrooms</option>
                  <option value="1">1 Bathroom</option>
                  <option value="2">2 Bathrooms</option>
                  <option value="3">3 Bathrooms</option>
                  <option value="4">4 Bathrooms</option>
                  <option value="5+">5+ Bathrooms</option>
                </select>
              </div>
            </>
          );
        } else {
          // Looking for property
          return (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Property Type Looking For <span className="text-red-500">*</span>
                </label>
                <select
                  name="propertyType"
                  value={formData.propertyType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent pr-8"
                >
                  <option value="">Select property type</option>
                  <option value="House to Buy">House to Buy</option>
                  <option value="House to Rent">House to Rent</option>
                  <option value="Apartment to Buy">Apartment to Buy</option>
                  <option value="Apartment to Rent">Apartment to Rent</option>
                  <option value="Land to Buy">Land to Buy</option>
                  <option value="Commercial Property">Commercial Property</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Bedrooms
                </label>
                <select
                  name="bedrooms"
                  value={formData.bedrooms}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent pr-8"
                >
                  <option value="">Any</option>
                  <option value="Studio">Studio</option>
                  <option value="1">1 Bedroom</option>
                  <option value="2">2 Bedrooms</option>
                  <option value="3">3 Bedrooms</option>
                  <option value="4">4 Bedrooms</option>
                  <option value="5+">5+ Bedrooms</option>
                </select>
              </div>
            </>
          );
        }

      case 'electronics':
      case 'phones':
      case 'furniture':
      case 'fashion':
        if (isSelling) {
          return (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Condition <span className="text-red-500">*</span>
                </label>
                <select
                  name="condition"
                  value={formData.condition}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent pr-8"
                >
                  <option value="">Select condition</option>
                  <option value="Brand New">Brand New</option>
                  <option value="Like New">Like New</option>
                  <option value="Good">Good</option>
                  <option value="Fair">Fair</option>
                  <option value="Used">Used</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Brand <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  name="brand"
                  value={formData.brand}
                  onChange={handleChange}
                  placeholder="e.g., Samsung, Apple, Sony"
                  required
                />
              </div>
            </>
          );
        } else {
          // Looking to buy
          return (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Condition
                </label>
                <select
                  name="condition"
                  value={formData.condition}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent pr-8"
                >
                  <option value="">Any condition</option>
                  <option value="Brand New">Brand New only</option>
                  <option value="Like New">Like New or better</option>
                  <option value="Good">Good or better</option>
                  <option value="Any">Any condition</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Brand
                </label>
                <Input
                  type="text"
                  name="brand"
                  value={formData.brand}
                  onChange={handleChange}
                  placeholder="e.g., Samsung, Apple, Sony or Any"
                />
              </div>
            </>
          );
        }

      default:
        return null;
    }
  };

  const getPlaceholderText = () => {
    if (!formData.category) return 'e.g., Brand New iPhone 15 Pro Max';
    
    const isSelling = listingType === 'selling';
    
    if (formData.category === 'jobs') {
      return isSelling 
        ? 'e.g., Senior Software Engineer' 
        : formData.category === 'real-estate'
        ? 'e.g., 3 Bedroom Flat for Rent in Lekki'
        : 'e.g., Brand New iPhone 15 Pro Max';
    }
    
    if (formData.category === 'services') {
      return isSelling 
        ? 'e.g., Professional Plumbing Services' 
        : 'e.g., Looking for Reliable Plumber';
    }
    
    if (formData.category === 'real-estate') {
      return isSelling 
        ? 'e.g., 3 Bedroom Flat for Rent in Lekki' 
        : 'e.g., Looking for 2 Bedroom Apartment in VI';
    }
    
    return isSelling 
      ? 'e.g., Brand New iPhone 15 Pro Max' 
      : 'e.g., Looking to Buy iPhone 15';
  };

  const getDescriptionPlaceholder = () => {
    if (!formData.category) return 'Describe your item or service in detail...';
    
    const isSelling = listingType === 'selling';
    
    if (formData.category === 'jobs') {
      return isSelling 
        ? 'Describe the job role, responsibilities, requirements, and company culture...' 
        : 'Describe your experience, skills, achievements, and what you\'re looking for...';
    }
    
    if (formData.category === 'services') {
      return isSelling 
        ? 'Describe your service, experience, and what makes you stand out...' 
        : 'Describe the service you need, specific requirements, and timeline...';
    }
    
    return isSelling 
      ? 'Describe your item in detail. Include condition, features, specifications, etc.' 
      : 'Describe what you\'re looking for, preferred features, and requirements...';
  };

  return (
    <>
      {/* Listing Type Selection Modal */}
      {showListingTypeModal && (
        <div className="fixed inset-0 bg-black bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-8 shadow-2xl">
            <div className="text-center mb-8">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                <i className="ri-question-line text-3xl text-green-600"></i>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">What would you like to do?</h2>
              <p className="text-gray-600">Choose the type of listing you want to create</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Selling Option */}
              <button
                onClick={() => handleListingTypeSelect('selling')}
                className="group relative bg-gradient-to-br from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 border-2 border-green-300 hover:border-green-500 rounded-xl p-8 transition-all duration-300 hover:shadow-lg cursor-pointer"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-green-500 rounded-full mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <i className="ri-price-tag-3-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">I'm Selling</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Post items, services, or job openings you want to offer
                </p>
                <ul className="text-left text-sm text-gray-700 space-y-2">
                  <li className="flex items-start">
                    <i className="ri-check-line text-green-600 mr-2 mt-0.5"></i>
                    <span>Sell products or items</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-green-600 mr-2 mt-0.5"></i>
                    <span>Offer services</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-green-600 mr-2 mt-0.5"></i>
                    <span>Post job openings</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-green-600 mr-2 mt-0.5"></i>
                    <span>Rent out property</span>
                  </li>
                </ul>
              </button>

              {/* Buying Option */}
              <button
                onClick={() => handleListingTypeSelect('buying')}
                className="group relative bg-gradient-to-br from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 border-2 border-blue-300 hover:border-blue-500 rounded-xl p-8 transition-all duration-300 hover:shadow-lg cursor-pointer"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-blue-500 rounded-full mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <i className="ri-shopping-cart-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">I'm Buying</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Post what you're looking for and let sellers come to you
                </p>
                <ul className="text-left text-sm text-gray-700 space-y-2">
                  <li className="flex items-start">
                    <i className="ri-check-line text-blue-600 mr-2 mt-0.5"></i>
                    <span>Looking to buy items</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-blue-600 mr-2 mt-0.5"></i>
                    <span>Need services</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-blue-600 mr-2 mt-0.5"></i>
                    <span>Seeking employment</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-blue-600 mr-2 mt-0.5"></i>
                    <span>Looking for property</span>
                  </li>
                </ul>
              </button>
            </div>

            <div className="mt-6 text-center">
              <button
                onClick={() => navigate('/')}
                className="text-gray-500 hover:text-gray-700 text-sm font-medium cursor-pointer"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white min-h-screen py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {listingType === 'selling' ? 'Post Your Listing' : 'Post Your Request'}
                </h1>
                <p className="text-gray-600">
                  {listingType === 'selling' 
                    ? 'Fill out the form below to list your item or service' 
                    : 'Tell us what you\'re looking for and let sellers come to you'
                  }
                </p>
              </div>
              <button
                onClick={() => setShowListingTypeModal(true)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line mr-2"></i>
                Change Type
              </button>
            </div>

            {/* Listing Type Badge */}
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-100 to-blue-100 rounded-full">
              <i className={`${listingType === 'selling' ? 'ri-price-tag-3-line text-green-600' : 'ri-shopping-cart-line text-blue-600'} mr-2`}></i>
              <span className="font-semibold text-gray-900">
                {listingType === 'selling' ? 'Selling / Offering' : 'Buying / Looking For'}
              </span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Category Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent pr-8"
              >
                <option value="">Select a category</option>
                <option value="real-estate">Real Estate</option>
                <option value="cars">Cars & Vehicles</option>
                <option value="electronics">Electronics</option>
                <option value="furniture">Furniture</option>
                <option value="jobs">Jobs</option>
                <option value="services">Services</option>
                <option value="fashion">Fashion & Beauty</option>
                <option value="phones">Phones & Tablets</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {listingType === 'selling' ? 'Ad Title' : 'What are you looking for?'} <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder={getPlaceholderText()}
                required
                maxLength={100}
              />
              <p className="text-xs text-gray-500 mt-1">Be specific and descriptive (max 100 characters)</p>
            </div>

            {/* Category-specific fields */}
            {formData.category && renderCategorySpecificFields()}

            {/* Price */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {formData.category === 'jobs' 
                  ? (listingType === 'selling' ? 'Salary Offered (₦)' : 'Expected Salary (₦)') 
                  : (listingType === 'selling' ? 'Price (₦)' : 'Budget (₦)')
                } {listingType === 'selling' && <span className="text-red-500">*</span>}
              </label>
              <Input
                type="text"
                name="price"
                value={formData.price}
                onChange={handleChange}
                placeholder={
                  listingType === 'selling'
                    ? 'e.g., 150000 or Contact for price'
                    : 'e.g., 150000 or Negotiable'
                }
                required={listingType === 'selling'}
              />
              <p className="text-xs text-gray-500 mt-1">
                {listingType === 'selling' 
                  ? 'Enter amount in Naira or type "Contact for price"'
                  : 'Enter your budget in Naira or type "Negotiable"'
                }
              </p>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location <span className="text-red-500">*</span>
              </label>
              {formData.category === 'jobs' || formData.category === 'real-estate' || formData.category === 'cars' ? (
                <select
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent pr-8"
                >
                  <option value="">Select location</option>
                  {locations.map((loc) => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                  {formData.category === 'jobs' && <option value="Remote">Remote</option>}
                </select>
              ) : (
                <Input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="e.g., Victoria Island, Lagos"
                  required
                />
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={8}
                required
                maxLength={500}
                placeholder={getDescriptionPlaceholder()}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">{formData.description.length}/500 characters</p>
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Images {listingType === 'selling' && formData.category !== 'jobs' && <span className="text-red-500">*</span>}
              </label>
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  dragActive 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  id="image-upload"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  className="hidden"
                  disabled={images.length >= 10}
                />
                <label
                  htmlFor="image-upload"
                  className={`cursor-pointer ${images.length >= 10 ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex flex-col items-center">
                    <i className="ri-image-add-line text-4xl text-gray-400 mb-2"></i>
                    <p className="text-sm font-medium text-gray-700 mb-1">
                      {images.length >= 10 
                        ? 'Maximum 10 images reached' 
                        : 'Click to upload or drag and drop'
                      }
                    </p>
                    <p className="text-xs text-gray-500">
                      PNG, JPG, JPEG up to 10MB each (Max 10 images)
                    </p>
                  </div>
                </label>
              </div>

              {/* Image Previews */}
              {imagePreviews.length > 0 && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                  {imagePreviews.map((preview, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={preview}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg border border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      >
                        <i className="ri-close-line text-sm"></i>
                      </button>
                      {index === 0 && (
                        <div className="absolute bottom-1 left-1 bg-green-600 text-white text-xs px-2 py-0.5 rounded">
                          Main
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <p className="text-xs text-gray-500 mt-2">
                {listingType === 'buying' 
                  ? 'Optional: Add reference images of what you\'re looking for'
                  : formData.category === 'jobs' 
                    ? 'Optional: Add company logo or office images'
                    : 'First image will be the main image. You can upload up to 10 images.'
                }
              </p>
            </div>

            {/* Contact Information - Only show if user is not logged in */}
            {!user && (
              <div className="border-t border-gray-200 pt-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Information</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {formData.category === 'jobs' 
                        ? (listingType === 'selling' ? 'Company Name' : 'Your Name')
                        : 'Your Name'
                      } <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="text"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleChange}
                      placeholder={
                        formData.category === 'jobs' && listingType === 'selling'
                          ? 'Company or organization name'
                          : 'Full name'
                      }
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="tel"
                      name="contactPhone"
                      value={formData.contactPhone}
                      onChange={handleChange}
                      placeholder="e.g., 08012345678"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="email"
                      name="contactEmail"
                      value={formData.contactEmail}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                      required
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Show profile info summary for logged in users */}
            {user && userProfile && (
              <div className="border-t border-gray-200 pt-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Contact Information</h2>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-full mr-3">
                      <i className="ri-user-line text-green-600"></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 mb-2">
                        <i className="ri-check-line text-green-600 mr-1"></i>
                        Using your profile information
                      </h3>
                      <div className="text-sm text-gray-700 space-y-1">
                        <p><span className="font-medium">Name:</span> {userProfile.full_name || 'Not set'}</p>
                        <p><span className="font-medium">Email:</span> {userProfile.email || user.email}</p>
                        <p><span className="font-medium">Phone:</span> {userProfile.phone || 'Not set'}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => navigate('/dashboard')}
                        className="text-xs text-green-600 hover:text-green-700 mt-2 cursor-pointer"
                      >
                        <i className="ri-edit-line mr-1"></i>
                        Update profile information
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Terms */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-600">
                By posting this {listingType === 'selling' ? 'ad' : 'request'}, you agree to our Terms of Service and confirm that all information provided is accurate. 
                Your contact information will be visible to interested {listingType === 'selling' ? 'buyers' : 'sellers'}.
              </p>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-4 pt-4">
              <Button 
                type="submit" 
                variant="primary" 
                size="lg" 
                className="flex-1 whitespace-nowrap"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Posting...' : `Post ${listingType === 'selling' ? 'Ad' : 'Request'}`}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                size="lg"
                onClick={() => navigate('/')}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </div>
          </form>

          {/* Help Text */}
          <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-900 mb-2">
              {listingType === 'selling' ? 'Posting Tips:' : 'Request Tips:'}
            </h3>
            <ul className="text-sm text-blue-800 space-y-1">
              {listingType === 'selling' ? (
                <>
                  <li>• Use a clear, descriptive title</li>
                  <li>• Upload high-quality images from multiple angles</li>
                  <li>• Be honest about the condition of your item</li>
                  <li>• Include all relevant details in the description</li>
                  <li>• Set a fair and competitive price</li>
                  <li>• Respond promptly to inquiries</li>
                </>
              ) : (
                <>
                  <li>• Be specific about what you're looking for</li>
                  <li>• Include your budget range if possible</li>
                  <li>• Mention any specific requirements or preferences</li>
                  <li>• Add reference images if available</li>
                  <li>• Respond quickly when sellers contact you</li>
                  <li>• Keep your listing updated</li>
                </>
              )}
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}
